import { useEffect, useRef } from "react";
import Phaser from "phaser";
import { GameScene } from "./GameScene";
import type { EventBus } from "../engine/EventBus";
import type { Interpreter } from "../engine/Interpreter";
import type { Blackboard } from "../engine/Blackboard";
import type { Rollback } from "../engine/Rollback";

interface PhaserRootProps {
  bus: EventBus;
  interpreter: Interpreter;
  bb: Blackboard;
  rollback?: Rollback | null;
  /** Only create the Phaser game when true. Once created it persists even if this goes false. */
  ready: boolean;
}

let _game: Phaser.Game | null = null;

export function PhaserRoot({ bus, interpreter, bb, rollback, ready }: PhaserRootProps) {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!containerRef.current) return;

    if (_game) {
      // Game already exists — just ensure canvas is in this container.
      containerRef.current.appendChild(_game.canvas);
      return;
    }

    // Don't create the Phaser game until the first game starts so that the
    // AudioContext is created within (or close to) a user gesture.
    if (!ready) return;

    const game = new Phaser.Game({
      type: Phaser.AUTO,
      width: 1280,
      height: 720,
      parent: containerRef.current,
      scene: [],
      scale: {
        mode: Phaser.Scale.FIT,
        autoCenter: Phaser.Scale.CENTER_BOTH,
      },
      backgroundColor: "#000000",
    });

    game.scene.add("GameScene", GameScene, true, {
      bus,
      interpreter,
      bb,
      rollback: rollback ?? undefined,
    });
    _game = game;
  }, [bus, interpreter, bb, rollback, ready]);

  return (
    <div
      ref={containerRef}
      style={{
        position: "absolute",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
      }}
    />
  );
}
